import { FC } from "react";
import { Href } from "expo-router";
import { AlertTriangleIcon } from "lucide-react-native";

import { VnBox } from "@/components/VnBox";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnLink } from "@/components/VnLink";
import { VnText } from "@/components/VnText/VnText";
import {
  DashboardCardType,
  WorkRequirementCardStateType,
} from "@/domain/dashboardCards";
import {
  getNextWorkRequirementStepLink,
  WorkRequirementType,
} from "@/domain/workRequirements";
import { I18nKeyType } from "@/i18n";
import { formatLongDate } from "@/utils/date";

import { DashboardCompactCard } from "./DashboardCompactCard";

type WorkRequirementCardVariantType = Extract<
  DashboardCardType,
  { kind: "WORK_REQUIREMENT" }
>;

type WorkRequirementCardPropsType = {
  card: WorkRequirementCardVariantType;
  isPrimary: boolean;
};

const CARD_COPY: Record<
  WorkRequirementCardStateType,
  {
    messageI18nKey: I18nKeyType;
    actionI18nKey: I18nKeyType;
    compactActionI18nKey: I18nKeyType;
  }
> = {
  START: {
    messageI18nKey: "native:workRequirements.card_START",
    actionI18nKey: "native:workRequirements.cardAction_START",
    compactActionI18nKey: "native:workRequirements.cardActionCompact_START",
  },
  CONTINUE: {
    messageI18nKey: "native:workRequirements.card_CONTINUE",
    actionI18nKey: "native:workRequirements.cardAction_CONTINUE",
    compactActionI18nKey: "common:continue",
  },
  UPLOAD_DOCUMENTS: {
    messageI18nKey: "native:workRequirements.card_UPLOAD_DOCUMENTS",
    actionI18nKey: "native:workRequirements.cardAction_UPLOAD_DOCUMENTS",
    compactActionI18nKey:
      "native:workRequirements.cardActionCompact_UPLOAD_DOCUMENTS",
  },
};

const getCardLink = ({
  state,
  workRequirement,
}: {
  state: WorkRequirementCardStateType;
  workRequirement: WorkRequirementType;
}): Href => {
  if (state === "UPLOAD_DOCUMENTS") {
    return {
      pathname: "/(authed)/work-requirements/[id]",
      params: { id: workRequirement.id },
    };
  }

  return getNextWorkRequirementStepLink({
    workRequirementId: workRequirement.id,
    step: state === "START" ? null : workRequirement.screeningLastStep,
    members: workRequirement.members,
  });
};

export const WorkRequirementCard: FC<WorkRequirementCardPropsType> = ({
  card,
  isPrimary,
}) => {
  const { state, workRequirement } = card;
  const { messageI18nKey, actionI18nKey, compactActionI18nKey } =
    CARD_COPY[state];
  const link = getCardLink({ state, workRequirement });
  const messageI18nOptions = {
    date: formatLongDate(workRequirement.workRequirementDate),
  };

  if (!isPrimary) {
    return (
      <DashboardCompactCard
        i18nKey="native:workRequirements.cardCompact"
        i18nOptions={messageI18nOptions}
        actions={
          <VnLink
            href={link}
            renderAsButton
            i18nKey={compactActionI18nKey}
            buttonProps={{ variant: "outline" }}
          />
        }
      />
    );
  }

  return (
    <VnCard className="flex flex-col">
      <VnBox className="flex flex-row items-center gap-4">
        <VnBox className="rounded-md bg-black/5 p-6">
          <VnIcon as={AlertTriangleIcon} size={42} strokeWidth={1} />
        </VnBox>

        <VnText
          variant="lg"
          className="flex-1"
          i18nKey={messageI18nKey}
          i18nOptions={messageI18nOptions}
        />
      </VnBox>

      <VnLink href={link} renderAsButton i18nKey={actionI18nKey} />
    </VnCard>
  );
};
